/**
 * URL encoding for the document "page view" deep link
 * (`/$org/documents/$docUid?page=N&hl=...`, spec 2026-07-17-14 §2).
 *
 * Boxes are inlined into the `hl` query param (rather than re-fetched from
 * the source property) so the highlight survives refresh/share without an
 * extra request. Format: `x,y,w,h;x,y,w,h;...`, each coordinate a
 * normalized [0,1] float rounded to 4 decimals to keep the URL short.
 */

export interface HighlightBox {
  x: number
  y: number
  w: number
  h: number
}

const DECIMALS = 4

function round(n: number): number {
  const factor = 10 ** DECIMALS
  return Math.round(n * factor) / factor
}

export function encodeHighlightBoxes(boxes: HighlightBox[] | undefined | null): string | undefined {
  if (!boxes || boxes.length === 0) return undefined
  return boxes.map(b => [b.x, b.y, b.w, b.h].map(round).join(',')).join(';')
}

export function decodeHighlightBoxes(hl: string | undefined | null): HighlightBox[] {
  if (!hl) return []
  return hl
    .split(';')
    .map(segment => segment.trim())
    .filter(Boolean)
    .map((segment): HighlightBox | null => {
      const parts = segment.split(',').map(Number)
      if (parts.length !== 4 || parts.some(n => Number.isNaN(n))) return null
      const [x, y, w, h] = parts
      return { x, y, w, h }
    })
    .filter((b): b is HighlightBox => b !== null)
}

/**
 * Where a page-view deep link came from — the asset/property or
 * contract/clause whose source badge built the link. Carried in the `from`
 * query param so clicking the highlight box in the viewer can navigate back
 * to it (the deep link itself carries no other trace of its origin).
 *
 * The row identifier (`propertyUid` / `clauseCode`) is optional: a caller
 * that cannot cheaply supply one still produces today's entity-only link,
 * which lands on the right tab without focusing a row. When it IS present,
 * the target page scrolls that row into view and pulses it — see
 * useFocusAndPulseRow and the `asset-property-row-*` / `clause-*`
 * data-testids it targets.
 */
export type PageHighlightOrigin =
  | { kind: 'asset'; assetUid: string; propertyUid?: string }
  | { kind: 'contract'; contractUid: string; clauseCode?: string }

export function encodeHighlightOrigin(origin: PageHighlightOrigin | undefined | null): string | undefined {
  if (!origin) return undefined
  if (origin.kind === 'asset') {
    if (!origin.assetUid) return undefined
    return origin.propertyUid ? `asset:${origin.assetUid}:${origin.propertyUid}` : `asset:${origin.assetUid}`
  }
  if (origin.kind === 'contract') {
    if (!origin.contractUid) return undefined
    return origin.clauseCode ? `contract:${origin.contractUid}:${origin.clauseCode}` : `contract:${origin.contractUid}`
  }
  return undefined
}

export function decodeHighlightOrigin(from: string | undefined | null): PageHighlightOrigin | undefined {
  if (!from) return undefined
  // Capped at 3 segments: kind:uid:rowId. A rowId containing ':' (unlikely —
  // property uids are UUIDs, clause codes are ontology identifiers) is kept
  // whole by re-joining anything past the second separator.
  const [kind, uid, ...rest] = from.split(':')
  const rowId = rest.length > 0 ? rest.join(':') : undefined
  if (!uid) return undefined
  if (kind === 'asset') return { kind: 'asset', assetUid: uid, ...(rowId ? { propertyUid: rowId } : {}) }
  if (kind === 'contract') return { kind: 'contract', contractUid: uid, ...(rowId ? { clauseCode: rowId } : {}) }
  return undefined
}

/**
 * Builds the { page, hl, from } search object for a deep link to a
 * document's page-view mode. `boxes` is omitted from the link entirely when
 * empty (page_only / no-page degradation — see PropertySourceBadge). `origin`
 * is omitted whenever the caller doesn't cheaply know its entity — the link
 * then behaves exactly as before (the viewer's highlight box stays inert).
 */
export function buildPageHighlightSearch(
  page: number,
  boxes?: HighlightBox[] | null,
  origin?: PageHighlightOrigin | null,
): { page: number; hl?: string; from?: string } {
  const hl = encodeHighlightBoxes(boxes)
  const from = encodeHighlightOrigin(origin)
  return {
    page,
    ...(hl ? { hl } : {}),
    ...(from ? { from } : {}),
  }
}

/**
 * Link of an evidence chip to its document. `source_details.page` opens the
 * page view on that page, and resolved `bounding_boxes` highlight the passage
 * (`from` lets a click on the highlight return to the asset's compliance row).
 * Anything but a positive integer page gives the plain document link.
 */
export function evidenceDocumentHref(
  org: string,
  documentUid: string,
  sourceDetails: Record<string, unknown> | null | undefined,
  assetUid?: string,
): string {
  const base = `/${org}/documents/${documentUid}`
  const page = sourceDetails?.page
  if (typeof page !== 'number' || !Number.isInteger(page) || page < 1) return base
  const boxes =
    sourceDetails?.geometry_status === 'resolved' && Array.isArray(sourceDetails.bounding_boxes)
      ? (sourceDetails.bounding_boxes as (HighlightBox & { page?: number })[]).filter(b => b.page === undefined || b.page === page)
      : []
  const { hl, from } = buildPageHighlightSearch(page, boxes, assetUid ? { kind: 'asset', assetUid } : null)
  const query = new URLSearchParams({ page: String(page), ...(hl ? { hl } : {}), ...(from ? { from } : {}) })
  return `${base}?${query.toString()}`
}
